// services
import loginService from "@/services/authentication_services/login-service";
import WorkCookies from "@/services/cookie_services/WorkCookies";
import router from "@/router";
import { isMobileDevice } from "@/plugins/MobileDetection";
import store from "@/store";

// handlers
import BASE_AUTHENTICATION_HANDLERS from '@/services/authentication_services/handler_registration/BASE_AUTHENTICATION_HANDLERS';
import CUSTOM_AUTHENTICATION_HANDLERS
    from "@/services/authentication_services/handler_registration/CUSTOM_AUTHENTICATION_HANDLERS";
import firebaseService from "@/services/firebase-service";

/**
 * Module to handle login and logout process
 */
var WorkAuthentication = (function () {

    const LOGOUT_PATH = '/rest/v4/logout';
    const TOTP_REQUIRED_MESSAGE = 'totp_verification_needed'
    var _logoutPending = false;

    const handleCSharpDeleteTokenAndLogout = async (taskId, deviceId) => {
        // window.console.log('handleCSharpDeleteToken for taskId ' + taskId);
        try {
            await firebaseService.delete(deviceId);
            await loginService.logout();
            return _forceLogout();
        } catch (e) {
            return _forceLogout();
        } finally {
            _logoutPending = false;
        }
    };
    window.handleCSharpDeleteTokenAndLogout = handleCSharpDeleteTokenAndLogout;

    /**
     * Is session|user authenticated.
     * @returns {boolean}
     */
    var isAuthenticated = function () {
        return !!WorkCookies.getAuthToken();
    }

    /**
     * Is authenticated for public/extern only.
     * @returns {boolean}
     */
    var isAuthenticatedForExtern = function () {
        let authToken = WorkCookies.getAuthToken();
        return authToken?.startsWith('-extern-');
    }

    /**
     * @param {String} username
     * @param {String} password
     * @returns {Promise<*>}
     */
    var invokeLogin = async function (username, password) {
        try {
            let response = await loginService.login(username, password);

            if (response?.data?.message === TOTP_REQUIRED_MESSAGE)
                return Promise.reject(response);

            return await AuthenticationHelper.initAuthentication(response);
        } catch (e) {
            await AuthenticationHelper.terminateAuthentication();
            return Promise.reject(e);
        }
    }

    /**
     * @param {String} username
     * @param {String} password
     * @param {String} code
     * @param {String} mode
     * @returns {Promise<*>}
     */
    var invokeLoginTotp = async function (username, password, code, mode) {
        try {
            const response = await loginService.loginTotp(username, password, code, mode);
            return await AuthenticationHelper.initAuthentication(response);
        } catch (e) {
            await AuthenticationHelper.terminateAuthentication();
            return Promise.reject(e);
        }
    }

    /**
     * @returns {Promise<void>}
     */
    var invokeLogout = async function () {
        if (_logoutPending) { return }

        _logoutPending = true;

        if (isMobileDevice()) {
            let taskId = await store.dispatch('mobileWrapperTasks/setTask', { callback: handleCSharpDeleteTokenAndLogout });

            // eslint-disable-next-line no-undef
            return invokeCSharpLogout(taskId); // RENAME OR REMOVING WILL BREAK MOBILEWRAPPER
        } else {
            try {
                await loginService.logout();
                return _forceLogout();
            } catch (e) {
                return _forceLogout();
            } finally {
                _logoutPending = false;
            }
        }
    }

    /**
     * Forced logout
     *
     * @returns {Promise<void>}
     * @private
     */
    var _forceLogout = async function () {
        await AuthenticationHelper.terminateAuthentication();

        await router.push({ name: 'LoginView' });
        await router.go();
    }

    /**
     * Runs registerd Handlers at login|logout to initialize or terminate authentication
     */
    var AuthenticationHelper = (function () {

        /**
         * @private
         */
        var _xAuthToken;

        /**
         * Registered handlers
         */
        const AUTHENTICATION_HANDLERS = [
            ...BASE_AUTHENTICATION_HANDLERS,
            ...CUSTOM_AUTHENTICATION_HANDLERS
        ];

        /**
         * Runs registerd handlers to initialize Authentication and WorkApp
         * @param {Object} response
         * @returns {Promise<Object>}
         */
        var initAuthentication = async function (response) {
            _xAuthToken = response.headers["x-auth-token"];

            setAuthTokenIntoWebView(_xAuthToken);
            await _runAuthenticationHandlers('initialize');
            return Promise.resolve(response);
        }

        var setAuthTokenIntoWebView = function (xAuthToken) {
            if (isMobileDevice()) {
                try {
                    // eslint-disable-next-line no-undef
                    invokeCSharpLogin(xAuthToken); // RENAME OR REMOVING WILL BREAK MOBILEWRAPPER
                } catch (e) {
                    // ignore error
                }
            }
        }

        /**
         * Runs registerd handlers to terminate Authentication and WorkApp
         * @returns {Promise<void>}
         */
        var terminateAuthentication = async function () {
            return await _runAuthenticationHandlers('terminate');
        }

        /**
         * @param {String} methodName
         * @returns {Promise<void>}
         * @private
         */
        var _runAuthenticationHandlers = async function (methodName) {
            for (let handler of AUTHENTICATION_HANDLERS) {
                await handler[methodName]({ xAuthToken: _xAuthToken });
            }
            return Promise.resolve();
        }

        return {
            initAuthentication: initAuthentication,
            terminateAuthentication: terminateAuthentication,
        }
    }());

    return {
        login: invokeLogin,
        loginTotp: invokeLoginTotp,
        logout: invokeLogout,
        isAuthenticated: isAuthenticated,
        isAuthenticatedForExtern: isAuthenticatedForExtern,
        terminateAuthentication: AuthenticationHelper.terminateAuthentication,
        LOGOUT_PATH: LOGOUT_PATH,
        TOTP_REQUIRED_MESSAGE: TOTP_REQUIRED_MESSAGE
    }
}());

export default WorkAuthentication;
